import React, { useState } from 'react';

import { makeStyles } from '@mui/styles';
import { Accordion, AccordionDetails, AccordionSummary, CardContent, Grid, Paper, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import schema_webhook_phi_json from '../../../lib/constants/schema_webhook_phi.json';
import schema_webhook_phi_xml from '../../../lib/constants/schema_webhook_phi.xml';
import schema_webhook_non_phi_json from '../../../lib/constants/schema_webhook_non_phi.json';
import schema_webhook_non_phi_xml from '../../../lib/constants/schema_webhook_non_phi.xml';
import { readFileAsString } from '../../../lib/helpers/helpers';
import { CodeHighlighter } from './index';
import { SSOV1 } from './Endpoints';

const useStyles = makeStyles((theme) => ({
  container: {
    margin: 10,
  },
  patientChartCard: {
    ...theme.globalClasses.patientChartCard,
    '& p': {
      marginTop: 10,
    },
  },
  title: {
    fontSize: '24px !important',
    textAlign: 'left',
    margin: 'auto auto 20px',
  },
  root: {
    width: '100%',
  },
  pre: {
    whiteSpace: 'pre-wrap',
  },
  childAccordion: {
    display: 'block',
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  headingGet: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
    color: theme.palette.info.main,
  },
  headingPost: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
    color: theme.palette.success.main,
  },
  headingPut: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
    color: theme.palette.warning.main,
  },
  headingDelete: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
    color: theme.palette.error.main,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
}));

const FAQs = () => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [subExpanded, setSubExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };
  const handleSubChange = (panel) => (event, isExpanded) => {
    setSubExpanded(isExpanded ? panel : false);
  };
  return (
    <Grid
      item
      xs={12}
    >
      <a id={'faqs'} />
      <Paper className={classes.patientChartCard}>
        <CardContent>
          <Typography
            variant={'h1'}
            className={classes.title}
          >
            Frequently Asked Questions
          </Typography>

          <Accordion
            expanded={expanded === 'faq_1'}
            onChange={handleChange('faq_1')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>I&apos;m getting HTML instead of JSON?</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.childAccordion}>
              <Typography>
                - Double-check you are using the <b>API Environment URL</b> instead of the <b>APP URL</b>.<br />
                <br />
                Staging:
                <pre className={classes.pre}>
                  <code>https://api-staging.dietid.com/api/:version/:endpoint</code>
                </pre>
                Production:
                <pre className={classes.pre}>
                  <code>https://api-prod.dietid.com/api/:version/:endpoint</code>
                </pre>
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'faq_2'}
            onChange={handleChange('faq_2')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>What URL do I use to access the API?</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.childAccordion}>
              <Typography>
                - To access the API, you will need to use the <b>API Environment URL</b> instead of the <b>APP URL</b>.
                <br />
                <br />
                Staging:
                <pre className={classes.pre}>
                  <code>https://api-staging.dietid.com/api/:version/:endpoint</code>
                </pre>
                Production:
                <pre className={classes.pre}>
                  <code>https://api-prod.dietid.com/api/:version/:endpoint</code>
                </pre>
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'faq_3'}
            onChange={handleChange('faq_3')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>What do I use for the bearer token?</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.childAccordion}>
              <Typography>
                - For the bearer token, you may use the <b>API Secret</b> to access endpoints on behalf of your
                Organization.
                <br />- You may also use the <b>SSO Token</b> received as the bearer token to access certain endpoints
                on behalf of that specific user.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'faq_4'}
            onChange={handleChange('faq_4')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>What data is required to create users via SSO?</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.childAccordion}>
              <Typography>
                - The minimum parameters that can be provided to create a new user via SSO, is a <b>partnerUserID</b>{' '}
                and an <b>email</b> address.
                <br />
                <br />
                Learn more about our SSO endpoint <a href={'#api_endpoints'}>here</a>.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'faq_5'}
            onChange={handleChange('faq_5')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>Can I pass other data via SSO?</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.childAccordion}>
              <Typography>
                - You can store other data via SSO by passing a JSON object to the parameter <b>additionalParams</b>.
                For example:
                <CodeHighlighter code={`{miscParam1:"abc123", miscParam2:"789xyz"}`} />
                <br />
                Learn more about our SSO endpoint <a href={'#api_endpoints'}>here</a>.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'faq_6'}
            onChange={handleChange('faq_6')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>What do I need to use the PartnerUserId for?</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.childAccordion}>
              <Typography>
                - The PartnerUserId is useful for when you may not know Diet ID&apos;s internal User ID.
                <br />
                - It is most commonly your organization&apos;s User ID for the specific user you want to store
                information for.
                <br />- It is referenced as x-partner-user-id when using within Headers.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'faq_7'}
            onChange={handleChange('faq_7')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>Do you refresh the tokens?</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.childAccordion}>
              <Typography>
                - We currently do not expire SSO tokens, but you can refresh the user&apos;s token by passing the same
                partnerUserId to the SSO endpoint.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'faq_8'}
            onChange={handleChange('faq_8')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>
                What endpoint do I use to pull the latest assessment results for a single user?
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.childAccordion}>
              <Typography>
                - To pull the latest assessment results for a specific user, the following endpoint may be used:
                <br />
                <pre className={classes.pre}>
                  <code>/api/v3/workflows/partnerUserId/:partnerUserId</code>
                </pre>
                <br />
                Learn more about our Workflow endpoints <a href={'#api_endpoints'}>here</a>.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'faq_9'}
            onChange={handleChange('faq_9')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>How do I embed the assessment into an iFrame?</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.childAccordion}>
              <Typography>
                - You can embed the assessment within your app easily with a web view by providing it either your
                organization&apos;s <b>App URL</b> or the user&apos;s <b>Tokenized SSO URL</b>. For example:
                <CodeHighlighter
                  code={`<iframe
src="https://abc123.dietid.com/?u=ABC123...789XYZ"
frameBorder="0"
seamless="seamless"
allow-scripts
allow-same-origin
style="width: 100%; height: 100%; border: none;">
Please check your browser's security policies.
</iframe>`}
                />
                <br />
                <br />
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion
            expanded={expanded === 'faq_10'}
            onChange={handleChange('faq_10')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1bh-content"
              id="panel1bh-header"
            >
              <Typography className={classes.heading}>Running into Cookie related issues with an iFrame?</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.childAccordion}>
              <Typography variant={'body1'}>1. Use allow-scripts and allow-same-origin attributes</Typography>
              <Typography>
                - Double check both the `allow-scripts` and `allow-same-origin` attributes are applied to the iFrame
                element. This will allow the iFrame to run JavaScript and access resources from its own domain,
                respectively. For example:
                <CodeHighlighter
                  code={`<iframe
src="https://abc123.dietid.com/?u=ABC123...789XYZ"
frameBorder="0"
seamless="seamless"
allow-scripts
allow-same-origin
style="width: 100%; height: 100%; border: none;">
Please check your browser's security policies.
</iframe>`}
                />
                <br />
                <br />
              </Typography>
              <Typography variant={'body1'}>2. Use the sandbox attribute (Caution)</Typography>
              <Typography>
                - Alternatively, you can add the sandbox attribute to the iframe element. This will enable a set of
                extra restrictions for the iframe, including blocking cookies, scripts, and the ability to use the
                window object. You can then use the allow-same-origin value to remove the sandbox restrictions for the
                iframe&apos;s own domain. For example:
                <CodeHighlighter
                  code={`<iframe
src="https://abc123.dietid.com/?u=ABC123...789XYZ"
frameBorder="0"
seamless="seamless"
sandbox="allow-same-origin allow-scripts"
style="width: 100%; height: 100%; border: none;">
Please check your browser's security policies.
</iframe>`}
                />
                <br />
                <br />
              </Typography>
              <Typography variant={'body1'}>3. Fallback Strategy (Limited Effectiveness)</Typography>
              <Typography>
                - A final fallback, you can use the onerror attribute to specify a JavaScript function that will be
                called when the iframe encounters an error. Keep in mind that the SECURITY_ERR error code may not always
                indicate a failure due to the cookie policy. It could also be caused by other security measures or
                issues with the server. For example:
                <CodeHighlighter
                  code={`<iframe
src="https://abc123.dietid.com/?u=ABC123...789XYZ"
frameBorder="0"
seamless="seamless"
sandbox="allow-same-origin allow-scripts"
onerror="handleError(event)"
style="width: 100%; height: 100%; border: none;">
Please check your browser's security policies.
</iframe>
<script>
  function handleError(event) {
  if ((event?.type === "error") || (event?.error?.code === "SECURITY_ERR")) {
      // the iframe failed to load due to the cookie policy
      window.location = "https://abc123.dietid.com/?u=ABC123...789XYZ" // your Organization's App URL
    }
  }
</script>`}
                />
                <br />
                <br />
              </Typography>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Paper>
    </Grid>
  );
};
export default FAQs;
